import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {observable, Observable, throwError} from 'rxjs';
import { environment } from '@env/environment';
import { ILogin } from '@app/modules/auth/interface/login';
import { IRegistrationConfirm } from '@app/modules/auth/interface/registration-confirm';
import { IRegistration } from '@app/modules/auth/interface/registration';
import { IPasswordRestoreConfirm } from '@app/modules/auth/interface/password-restore-confirm';
import { IPasswordRestore } from '@app/modules/auth/interface/password-restore';
import { Store } from '@ngxs/store';
import { ICognitoUser } from '@app/modules/auth/interface/cognito-user';
import { IPasswordChange } from '@app/modules/auth/interface/password-change';
import { map, tap, catchError } from 'rxjs/operators';
import { AuthTokenService } from '@app/modules/auth/service/auth-token.service';
import {UserActions} from '@app/modules/user/store/user.actions';
import {AuthActions} from '@app/modules/auth/store/auth.actions';
import {ModelAttributes} from "@app/core/interface/common";
import {IPasswordReset} from "@app/modules/auth/interface/password-reset";

interface AuthResponse {
  token: string;
  refreshToken: string;
}

interface TokenResponse {
  tokens: {
    access: string;
    refresh: string;
  };
}

@Injectable()
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/accounts`;

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store,
    private readonly tokenService: AuthTokenService
  ) {}

  public registration(user: IRegistration): Observable<string> {
    return this.http.post<{ userSub: string }>(`${this.apiUrl}/create`, user)
      .pipe(map(response => response.userSub));
  }

  public registrationConfirm(user: IRegistrationConfirm): Observable<boolean> {
    return this.http.post<void>(`${this.apiUrl}/confirm`, user)
      .pipe(map(() => true));
  }

  public updateCognitoUser(user: ICognitoUser): Observable<boolean> {
    return this.http.put<void>(`${this.apiUrl}/user`, user)
      .pipe(map(() => true));
  }

  public login(user: ILogin): Observable<string> {
    return this.http.post<TokenResponse>(`${this.apiUrl}/login`, user)
      .pipe(
        tap(response => {
          this.tokenService.saveTokens(response.tokens.access, response.tokens.refresh);
        }),
        map(response => response.tokens.access)
      );
  }

  /**
   * Sign in with a Google ID token. Posts to the backend's /auth/google, which
   * verifies the token and returns the app's own access/refresh tokens — the
   * same shape as password login, so the downstream flow is identical.
   */
  public googleLogin(idToken: string): Observable<string> {
    return this.http.post<TokenResponse>(`${environment.apiUrl}/auth/google`, { idToken })
      .pipe(
        tap(response => {
          this.tokenService.saveTokens(response.tokens.access, response.tokens.refresh);
        }),
        map(response => response.tokens.access)
      );
  }

  public logout(): Observable<string> {
    let refreshToken = this.tokenService.getAccessToken();

    if (!refreshToken) {
      refreshToken = ' ';
    }

    return this.http.post<AuthResponse>(`${this.apiUrl}/logout`, { refresh: refreshToken })
      .pipe(
        tap(response => {
          this.tokenService.clearTokens();

        }),
        map(response => response.token)
      );

  }

  public refreshToken(): Observable<string> {
    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      return throwError('No refresh token available');
    }

    return this.http.post<TokenResponse>(`${this.apiUrl}/reissue-tokens`, { refresh: refreshToken })
      .pipe(
        tap(response => {
          this.tokenService.saveTokens(response.tokens.access, response.tokens.refresh);
        }),
        map(response => response.tokens.access)
      );
  }

  public passwordChange(passwordChange: IPasswordChange): Observable<boolean> {
    const model: IPasswordChange = {
      oldPassword: passwordChange.oldPassword,
      newPassword: passwordChange.newPassword,
      email: passwordChange.email
    };

    return this.http.post<void>(`${this.apiUrl}/passwordChange`, model)
      .pipe(map(() => true));
  }

  public passwordReset(passwordReset: IPasswordReset): Observable<boolean> {
    return this.http.post<void>(`${this.apiUrl}/passwordReset`, passwordReset)
      .pipe(map(() => true));
  }


}
